import React, { useRef, useEffect, useCallback } from 'react';
import type { AIChatMessage, AIChatStep } from './types';
import { AIChatMessage as AIChatMessageComponent } from './AIChatMessage';
import { AIChatTypingIndicator } from './AIChatTypingIndicator';
import { AIChatWaveIndicator } from './AIChatWaveIndicator';
import { AIChatSteps } from './AIChatSteps';
import { MessageListContainer, MessageListEmptyStateWrapper } from './AIChat.Styled';

const SCROLL_THRESHOLD = 40;

export type AIChatMessageListProps = {
	messages: AIChatMessage[];
	isLoading?: boolean;
	showWaveIndicator?: boolean;
	/** What the assistant has done so far in this run; replaces the loading phrase when given. */
	steps?: AIChatStep[];
	assistantAvatarSrc?: string;
	assistantAvatarAlt?: string;
	autoScroll?: boolean;
	emptyState?: React.ReactNode;
};

export function AIChatMessageList({
	messages,
	isLoading,
	showWaveIndicator,
	steps,
	assistantAvatarSrc,
	assistantAvatarAlt,
	autoScroll = true,
	emptyState,
}: AIChatMessageListProps): React.ReactElement {
	const containerRef = useRef<HTMLDivElement>(null);
	const nearBottomRef = useRef(true);

	const scrollToBottom = useCallback(() => {
		const el = containerRef.current;
		if (!el || typeof el.scrollTo !== 'function') return;
		el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
	}, []);

	const checkNearBottom = useCallback(() => {
		const el = containerRef.current;
		if (!el) {
			nearBottomRef.current = true;
			return;
		}
		nearBottomRef.current =
			el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
	}, []);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		let rafId: number;
		const throttledScroll = () => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(checkNearBottom);
		};
		el.addEventListener('scroll', throttledScroll, { passive: true });
		return () => {
			el.removeEventListener('scroll', throttledScroll);
			cancelAnimationFrame(rafId);
		};
	}, [checkNearBottom]);

	useEffect(() => {
		if (!autoScroll) return;
		if (nearBottomRef.current) {
			scrollToBottom();
		}
	}, [messages.length, isLoading, steps?.length, autoScroll, scrollToBottom]);

	const isEmpty = messages.length === 0 && !isLoading;

	return (
		<MessageListContainer ref={containerRef} role="log" aria-live="polite" aria-label="Chat messages">
			{isEmpty && emptyState
				? <MessageListEmptyStateWrapper>{emptyState}</MessageListEmptyStateWrapper>
				: messages.map((message, index) => {
					const continued =
						index > 0 &&
						messages[index - 1].role === message.role &&
						!messages[index - 1].isError &&
						!message.isError;
					return (
						<AIChatMessageComponent
							key={message.id}
							message={message}
							continued={continued}
							assistantAvatarSrc={assistantAvatarSrc}
							assistantAvatarAlt={assistantAvatarAlt}
						/>
					);
				})
			}
			{isLoading && (
				steps?.length
					? <AIChatSteps steps={steps} assistantAvatarSrc={assistantAvatarSrc} />
					: showWaveIndicator
						? <AIChatWaveIndicator assistantAvatarSrc={assistantAvatarSrc} />
						: <AIChatTypingIndicator assistantAvatarSrc={assistantAvatarSrc} />
			)}
		</MessageListContainer>
	);
}
